import { createClient } from '@supabase/supabase-js';

// Local API mode detection
const useLocal = (import.meta.env?.VITE_USE_LOCAL_API === 'true') || (!import.meta.env?.VITE_SUPABASE_URL && !import.meta.env?.VITE_SUPABASE_ANON_KEY);
const apiBase = import.meta.env?.VITE_API_BASE_URL || 'http://localhost:4000/api';

// Environment variables validation with detailed debugging
const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

if (!useLocal) {
  console.log('🔍 Supabase Environment Debug:', {
    url: supabaseUrl ? `${supabaseUrl?.substring(0, 20)}...` : 'UNDEFINED',
    key: supabaseAnonKey ? `${supabaseAnonKey?.substring(0, 20)}...` : 'UNDEFINED',
    urlType: typeof supabaseUrl,
    keyType: typeof supabaseAnonKey
  });
}

// Enhanced validation with specific error messages
if (!useLocal && !supabaseUrl) {
  console.error('❌ VITE_SUPABASE_URL está vazia ou não definida');
  throw new Error(
    '⚠️ Configuração Supabase: VITE_SUPABASE_URL não encontrada no arquivo .env\n' + '📝 Adicione: VITE_SUPABASE_URL=https://seu-projeto.supabase.co'
  );
}

if (!useLocal && !supabaseAnonKey) {
  console.error('❌ VITE_SUPABASE_ANON_KEY está vazia ou não definida');
  throw new Error(
    '⚠️ Configuração Supabase: VITE_SUPABASE_ANON_KEY não encontrada no arquivo .env\n' + '📝 Adicione: VITE_SUPABASE_ANON_KEY=sua-chave-anonima-aqui'
  );
}

// Trim whitespace and validate URL format
const cleanUrl = supabaseUrl?.trim();
const cleanKey = supabaseAnonKey?.trim();

// More robust URL validation
const isValidUrl = (url) => {
  try {
    const urlObj = new URL(url);
    return urlObj?.protocol === 'https:' || urlObj?.protocol === 'http:';
  } catch {
    return false;
  }
};

if (!useLocal && !isValidUrl(cleanUrl)) {
  console.error('❌ URL do Supabase inválida:', cleanUrl);
  throw new Error(
    '⚠️ VITE_SUPABASE_URL deve ser uma URL válida\n' + '✅ Exemplo correto: https://projeto.supabase.co\n' +
    `❌ URL fornecida: ${cleanUrl}`
  );
}

if (!useLocal) {
  console.log('✅ Configuração Supabase validada com sucesso');
}

// Create Supabase client with validated credentials
export const supabase = useLocal ? null : createClient(cleanUrl, cleanKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  },
  global: {
    headers: {
      'x-application-name': 'FioGo'
    }
  }
});

// Helpers to ensure payloads match the database schema (avoid unknown columns)
const pickKeys = (obj, keys) => {
  const out = {};
  keys.forEach((k) => {
    if (obj && Object.prototype.hasOwnProperty.call(obj, k) && obj[k] !== undefined) {
      out[k] = obj[k];
    }
  });
  return out;
};

const sanitizePrinterPayload = (data) => pickKeys(data, [
  'user_id',
  'name',
  'brand',
  'model',
  'image_url',
  'image_urls',
  'status',
  'hourly_rate',
  'power_consumption',
  'build_volume_x',
  'build_volume_y',
  'build_volume_z',
  'precision_mm',
  'max_speed_mm_s',
  'notes',
  'manufacturer_id',
  'model_id',
  'created_at',
  'updated_at'
]);

const sanitizeFilamentPayload = (data) => pickKeys(data, [
  'user_id',
  'brand',
  'material',
  'color',
  'color_hex',
  'price_per_kg',
  'stock_grams',
  'density',
  'image_url',
  'image_urls',
  'supplier',
  'notes',
  'purchase_date',
  'created_at',
  'updated_at'
]);

  // Enhanced helper functions for common operations
  export const supabaseHelpers = {
  // Test connection
  async testConnection() {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('count', { count: 'exact', head: true });
      
      if (error) {
        console.warn('⚠️ Conexão Supabase estabelecida, mas tabela user_profiles não encontrada:', error?.message);
        return { connected: true, tableExists: false, error };
      }
      
      console.log('✅ Conexão Supabase e esquema validados');
      return { connected: true, tableExists: true, error: null };
    } catch (err) {
      console.error('❌ Erro na conexão Supabase:', err);
      return { connected: false, tableExists: false, error: err };
    }
  },

  // Marketplace CRUD
  async createMarketplace(marketplaceData) {
    try {
      if (useLocal) {
        const res = await fetch(`${apiBase}/marketplaces`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(marketplaceData) });
        if (!res.ok) return { data: null, error: new Error(await res.text()) };
        const data = await res.json();
        return { data, error: null };
      }
      const { data, error } = await supabase?.from('marketplaces')?.insert([marketplaceData])?.select()?.single();
      if (!error) {
        console.log('✅ Marketplace criado:', data?.name);
      }
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao criar marketplace:', error);
      return { data: null, error };
    }
  },

  async getUserMarketplaces(userId) {
    try {
      if (useLocal) {
        const res = await fetch(`${apiBase}/users/${userId || 'guest'}/marketplaces`);
        if (!res.ok) return { data: [], error: new Error(await res.text()) };
        const data = await res.json();
        return { data, error: null };
      }
      const { data, error } = await supabase?.from('marketplaces')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao buscar marketplaces:', error);
      return { data: [], error };
    }
  },

  async updateMarketplace(marketplaceId, updates) {
    try {
      if (useLocal) {
        const res = await fetch(`${apiBase}/marketplaces/${marketplaceId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates) });
        if (!res.ok) return { data: null, error: new Error(await res.text()) };
        const data = await res.json();
        return { data, error: null };
      }
      const { data, error } = await supabase?.from('marketplaces')?.update(updates)?.eq('id', marketplaceId)?.select()?.single();
      if (!error) {
        console.log('✅ Marketplace atualizado:', marketplaceId);
      }
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao atualizar marketplace:', error);
      return { data: null, error };
    }
  },

  async deleteMarketplace(marketplaceId) {
    try {
      if (useLocal) {
        const res = await fetch(`${apiBase}/marketplaces/${marketplaceId}`, { method: 'DELETE' });
        if (!res.ok) return { data: null, error: new Error(await res.text()) };
        const data = await res.json();
        return { data, error: null };
      }
      const { data, error } = await supabase?.from('marketplaces')?.delete()?.eq('id', marketplaceId);
      if (!error) {
        console.log('🗑️ Marketplace removido:', marketplaceId);
      }
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao deletar marketplace:', error);
      return { data: null, error };
    }
  },

  // Marketplace stats helper
  async getMarketplaceStats(marketplaceId, userId) {
    try {
      if (useLocal) {
        const url = new URL(`${apiBase}/marketplaces/${marketplaceId}/stats`);
        if (userId) url.searchParams.set('userId', userId);
        const res = await fetch(url.toString());
        if (!res.ok) return { data: null, error: new Error(await res.text()) };
        const data = await res.json();
        return { data, error: null };
      }
      // Supabase aggregation: join project_marketplaces with printing_projects
      const query = supabase?.from('project_marketplaces')?.select(`
        id,
        quantity,
        project:printing_projects(id, user_id, final_price, price_per_unit, quantity)
      `)
        ?.eq('marketplace_id', marketplaceId)
        ?.eq('project.user_id', userId);

      const { data, error } = query ? await query : { data: [], error: null };
      if (error) {
        console.warn('⚠️ Erro ao agregar estatísticas de marketplace:', error?.message);
        return { data: { usageCount: 0, allocatedUnits: 0, allocatedValue: 0, totalValue: 0 }, error };
      }

      const seenProjects = new Set();
      let allocatedUnits = 0;
      let allocatedValue = 0;

      (data || []).forEach((row) => {
        const proj = row?.project || {};
        const projId = proj?.id;
        if (projId) seenProjects.add(projId);
        const units = parseInt(row?.quantity ?? 0, 10) || 0;
        allocatedUnits += units;
        const unitPrice = (
          parseFloat(proj?.price_per_unit ?? 0) ||
          ((parseFloat(proj?.final_price ?? 0) || 0) / (parseInt(proj?.quantity ?? 1, 10) || 1))
        );
        allocatedValue += units * (unitPrice || 0);
      });

      const usageCount = seenProjects.size;
      const result = { usageCount, allocatedUnits, allocatedValue, totalValue: allocatedValue };
      return { data: result, error: null };
    } catch (error) {
      console.error('❌ Erro ao obter estatísticas de marketplace:', error);
      return { data: null, error };
    }
  },

  // Project marketplaces pivot
  async addProjectMarketplace(relData) {
    try {
      if (useLocal) {
        const res = await fetch(`${apiBase}/project_marketplaces`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(relData) });
        if (!res.ok) return { data: null, error: new Error(await res.text()) };
        const data = await res.json();
        return { data, error: null };
      }
      const { data, error } = await supabase?.from('project_marketplaces')?.insert([relData])?.select()?.single();
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao adicionar marketplace ao projeto:', error);
      return { data: null, error };
    }
  },

  async removeProjectMarketplace(relId) {
    try {
      if (useLocal) {
        const res = await fetch(`${apiBase}/project_marketplaces/${relId}`, { method: 'DELETE' });
        if (!res.ok) return { data: null, error: new Error(await res.text()) };
        const data = await res.json();
        return { data, error: null };
      }
      const { data, error } = await supabase?.from('project_marketplaces')?.delete()?.eq('id', relId);
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao remover marketplace do projeto:', error);
      return { data: null, error };
    }
  },

  // Auth helpers
  async signUp(email, password, userData = {}) {
    try {
      if (useLocal) {
        const apiBase = import.meta?.env?.VITE_API_BASE_URL || '/api';
        const res = await fetch(`${apiBase}/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: userData?.name, email, password })
        });
        const json = await res.json();
        if (!res.ok || json?.error) {
          return { data: null, error: { message: json?.error || 'Erro no cadastro' } };
        }
        const user = json?.user;
        // Persistência simples de sessão local (apenas usuário, sem JWT)
        try { localStorage.setItem('session_user', JSON.stringify(user)); } catch {}
        return { data: { user }, error: null };
      }
      const result = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: userData
        }
      });
      const translate = (msg) => {
        const m = String(msg || '').toLowerCase();
        if (m.includes('rate limit') || m.includes('too many')) return 'Muitas tentativas. Tente novamente em alguns minutos.';
        if (m.includes('invalid email')) return 'Email inválido.';
        if (m.includes('password should be at least') || m.includes('weak password')) return 'Senha muito curta ou fraca.';
        if (m.includes('email already registered') || m.includes('already registered')) return 'Este email já está cadastrado.';
        return msg || 'Erro ao cadastrar';
      };
      if (result?.error) result.error.message = translate(result?.error?.message);
      console.log('🔐 Cadastro realizado:', result?.data?.user ? 'Sucesso' : 'Falhou');
      return { data: result?.data, error: result?.error || null };
    } catch (error) {
      console.error('❌ Erro no cadastro:', error);
      throw error;
    }
  },

  async signIn(email, password) {
    try {
      if (useLocal) {
        const apiBase = import.meta?.env?.VITE_API_BASE_URL || '/api';
        const res = await fetch(`${apiBase}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password })
        });
        const json = await res.json();
        if (!res.ok || json?.error) {
          return { data: null, error: { message: json?.error || 'Erro no login' } };
        }
        const user = json?.user;
        try { localStorage.setItem('session_user', JSON.stringify(user)); } catch {}
        return { data: { user }, error: null };
      }
      const result = await supabase?.auth?.signInWithPassword({ email, password });
      const translate = (msg) => {
        const m = String(msg || '').toLowerCase();
        if (m.includes('invalid login credentials')) return 'Credenciais de login inválidas.';
        if (m.includes('invalid email')) return 'Email inválido.';
        if (m.includes('email not confirmed')) return 'Email não confirmado. Verifique sua caixa de entrada.';
        if (m.includes('user not found')) return 'Usuário não encontrado.';
        if (m.includes('rate limit') || m.includes('too many')) return 'Muitas tentativas. Tente novamente em alguns minutos.';
        if (m.includes('network')) return 'Erro de rede. Verifique sua conexão.';
        return msg || 'Erro ao fazer login';
      };
      if (result?.error) result.error.message = translate(result?.error?.message);
      console.log('🔓 Login realizado:', result?.data?.user ? 'Sucesso' : 'Falhou');
      return { data: result?.data, error: result?.error || null };
    } catch (error) {
      console.error('❌ Erro no login:', error);
      throw error;
    }
  },

  async signOut() {
    try {
      if (useLocal) {
        return { error: null };
      }
      const result = await supabase?.auth?.signOut();
      console.log('🚪 Logout realizado');
      return result;
    } catch (error) {
      console.error('❌ Erro no logout:', error);
      throw error;
    }
  },

  // OAuth login helper (e.g., Google)
  async signInWithOAuth(provider, { redirectTo } = {}) {
    try {
      if (useLocal) {
        // No redirect in local mode; simulate success
        return { data: { provider, url: null }, error: null };
      }
      const result = await supabase?.auth?.signInWithOAuth({
        provider,
        options: {
          redirectTo: redirectTo || (typeof window !== 'undefined' ? window.location.origin : undefined)
        }
      });
      const translate = (msg) => {
        const m = String(msg || '').toLowerCase();
        if (m.includes('oauth') && m.includes('canceled')) return 'Login cancelado.';
        if (m.includes('provider')) return 'Erro no provedor de login. Tente novamente.';
        return msg || 'Erro no login OAuth';
      };
      if (result?.error) result.error.message = translate(result?.error?.message);
      console.log(`🔗 OAuth (${provider}) iniciado`, result?.data?.url ? 'com redirect' : 'sem redirect');
      return { data: result?.data, error: result?.error || null };
    } catch (error) {
      console.error(`❌ Erro no OAuth (${provider}):`, error);
      throw error;
    }
  },

  async getCurrentUser() {
    try {
      if (useLocal) {
        return { id: 'guest', email: 'guest@local' };
      }
      const { data: { user } } = await supabase?.auth?.getUser();
      return user;
    } catch (error) {
      console.error('❌ Erro ao obter usuário atual:', error);
      return null;
    }
  },

  async getUserProfile(userId) {
    try {
      if (useLocal) {
        const res = await fetch(`${apiBase}/user_profiles/${userId || 'guest'}`);
        if (!res.ok) return { data: null, error: new Error(await res.text()) };
        const data = await res.json();
        return { data, error: null };
      }
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
      
      if (error && error?.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('❌ Erro ao buscar perfil:', error);
      }
      
      return { data, error };
    } catch (error) {
      console.error('❌ Erro na busca do perfil:', error);
      return { data: null, error };
    }
  },

  // Storage helpers with error handling
  async uploadFile(bucket, filePath, file) {
    try {
      const { data, error } = await supabase?.storage?.from(bucket)?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });
      
      if (error) {
        console.error(`❌ Erro no upload para ${bucket}/${filePath}:`, error);
      } else {
        console.log(`✅ Arquivo enviado: ${bucket}/${filePath}`);
      }
      
      return { data, error };
    } catch (error) {
      console.error('❌ Erro crítico no upload:', error);
      return { data: null, error };
    }
  },

  async getSignedUrl(bucket, filePath, expiresIn = 3600) {
    try {
      const { data, error } = await supabase?.storage?.from(bucket)?.createSignedUrl(filePath, expiresIn);
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao gerar URL assinada:', error);
      return { data: null, error };
    }
  },

  async deleteFile(bucket, filePath) {
    try {
      const { data, error } = await supabase?.storage?.from(bucket)?.remove([filePath]);
      
      if (!error) {
        console.log(`🗑️ Arquivo removido: ${bucket}/${filePath}`);
      }
      
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao deletar arquivo:', error);
      return { data: null, error };
    }
  },

  // Printer CRUD with enhanced error handling
  async createPrinter(printerData) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/printers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(printerData)
        });
        const data = await resp.json().catch(() => null);
        const error = resp.ok ? null : (data?.error || 'Erro ao criar impressora');
        if (!error) console.log('✅ Impressora criada (local):', data?.name);
        return { data, error };
      }

      const clean = sanitizePrinterPayload(printerData);
      const { data, error } = await supabase?.from('printers')?.insert([clean])?.select()?.single();
      if (!error) {
        console.log('✅ Impressora criada:', data?.name);
      }
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao criar impressora:', error);
      return { data: null, error };
    }
  },

  async getUserPrinters(userId) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/users/${userId || 'guest'}/printers`);
        const data = await resp.json().catch(() => []);
        const error = resp.ok ? null : 'Erro ao buscar impressoras';
        return { data, error };
      }

      // Incluir referências de fabricante e modelo para exibir nomes na UI
      const { data, error } = await supabase?.from('printers')?.select(`
        *,
        manufacturerRef:printer_manufacturers(name, logo_url),
        modelRef:printer_models(name, build_volume_x, build_volume_y, build_volume_z, nozzle_diameter)
      `)?.eq('user_id', userId)?.order('created_at', { ascending: false });
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao buscar impressoras:', error);
      return { data: [], error };
    }
  },

  async updatePrinter(printerId, updates) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/printers/${printerId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updates)
        });
        const data = await resp.json().catch(() => null);
        const error = resp.ok ? null : (data?.error || 'Erro ao atualizar impressora');
        if (!error) console.log('✅ Impressora atualizada (local):', printerId);
        return { data, error };
      }
      const clean = sanitizePrinterPayload(updates);
      const { data, error } = await supabase?.from('printers')?.update(clean)?.eq('id', printerId)?.select()?.single();
      if (!error) {
        console.log('✅ Impressora atualizada:', printerId);
      }
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao atualizar impressora:', error);
      return { data: null, error };
    }
  },

  async deletePrinter(printerId) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/printers/${printerId}`, { method: 'DELETE' });
        const data = await resp.json().catch(() => null);
        const error = resp.ok ? null : (data?.error || 'Erro ao deletar impressora');
        if (!error) console.log('🗑️ Impressora removida (local):', printerId);
        return { data, error };
      }

      const { data, error } = await supabase?.from('printers')?.delete()?.eq('id', printerId);
      if (!error) {
        console.log('🗑️ Impressora removida:', printerId);
      }
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao deletar impressora:', error);
      return { data: null, error };
    }
  },

  // Enhanced filament helpers with history tracking
  async getFilamentUsageHistory(filamentId) {
    try {
      // Fallback seguro em modo local: retorna lista vazia para não quebrar a UI
      if (useLocal) {
        return { data: [], error: null };
      }

      const query = supabase?.from('filament_usage_history')?.select(`
        *,
        project:printing_projects(id, name, created_at, image_urls),
        filament:filaments(brand, material, color)
      `)?.eq('filament_id', filamentId)?.order('usage_date', { ascending: false });

      const { data, error } = query ? await query : { data: [], error: null };
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao buscar histórico de uso do filamento:', error);
      return { data: [], error };
    }
  },

  async createFilamentUsage(usageData) {
    const { data, error } = await supabase?.from('filament_usage_history')?.insert(usageData)?.select();
    
    return { data, error };
  },

  // Printer manufacturer and model helpers
  async getPrinterManufacturers() {
    if (useLocal) {
      return { data: [], error: null };
    }
    const { data, error } = await supabase?.from('printer_manufacturers')?.select('*')?.order('name');
    return { data, error };
  },

  async getPrinterModelsByManufacturer(manufacturerId) {
    if (useLocal) {
      return { data: [], error: null };
    }
    const { data, error } = await supabase?.from('printer_models')?.select('*')?.eq('manufacturer_id', manufacturerId)?.order('name');
    return { data, error };
  },

  async getPrinterModel(modelId) {
    if (useLocal) {
      return { data: null, error: null };
    }
    const { data, error } = await supabase?.from('printer_models')?.select(`
        *,
        manufacturer:printer_manufacturers(name, logo_url)
      `)?.eq('id', modelId)?.single();
    return { data, error };
  },

  // Enhanced printer management
  async updatePrinterStatus(printerId, newStatus, reason = null) {
    if (useLocal) {
      console.log('ℹ️ Atualização de status (local stub):', { printerId, newStatus, reason });
      return { data: { success: true }, error: null };
    }
    const { data, error } = await supabase?.rpc('update_printer_status', {
      printer_uuid: printerId,
      new_status_value: newStatus,
      reason_text: reason
    });
    return { data, error };
  },

  async getPrinterStatusHistory(printerId) {
    if (useLocal) {
      return { data: [], error: null };
    }
    const { data, error } = await supabase?.from('printer_status_history')?.select(`
        *,
        changed_by_user:user_profiles(full_name, email)
      `)?.eq('printer_id', printerId)?.order('changed_at', { ascending: false });
    return { data, error };
  },

  // Enhanced project management with advanced costing
  async createProjectWithAdvancedCosting(projectData) {
    try {
      if (useLocal) {
        const payload = {
          ...projectData,
          printer_purchase_cost: projectData?.printer_purchase_cost ?? 0,
          expected_printer_lifetime_years: projectData?.expected_printer_lifetime_years ?? 3,
          daily_usage_hours: projectData?.daily_usage_hours ?? 6.0,
          repair_cost_percentage: projectData?.repair_cost_percentage ?? 5.0,
          setup_time_minutes: projectData?.setup_time_minutes ?? 15,
          setup_cost_per_hour: projectData?.setup_cost_per_hour ?? 25.0,
          postprocessing_time_minutes: projectData?.postprocessing_time_minutes ?? 30,
          postprocessing_cost_per_hour: projectData?.postprocessing_cost_per_hour ?? 25.0,
          other_costs: projectData?.other_costs ?? 0,
          vat_percentage: projectData?.vat_percentage ?? 0
        };
        const resp = await fetch(`${apiBase}/projects`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await resp.json().catch(() => null);
        const error = resp.ok ? null : (data?.error || 'Erro ao criar projeto (local)');
        if (!error) console.log('✅ Projeto criado (local):', data?.name);
        return { data, error };
      }
      const { data, error } = await supabase?.from('printing_projects')?.insert({
        ...projectData,
        printer_purchase_cost: projectData?.printer_purchase_cost || 0,
        expected_printer_lifetime_years: projectData?.expected_printer_lifetime_years || 3,
        daily_usage_hours: projectData?.daily_usage_hours || 6.0,
        repair_cost_percentage: projectData?.repair_cost_percentage || 5.0,
        setup_time_minutes: projectData?.setup_time_minutes || 15,
        setup_cost_per_hour: projectData?.setup_cost_per_hour || 25.0,
        postprocessing_time_minutes: projectData?.postprocessing_time_minutes || 30,
        postprocessing_cost_per_hour: projectData?.postprocessing_cost_per_hour || 25.0,
        other_costs: projectData?.other_costs || 0,
        vat_percentage: projectData?.vat_percentage || 0
      })?.select()?.single();
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao criar projeto com custos avançados:', error);
      return { data: null, error };
    }
  },

  // Image upload helpers
  async uploadPrinterImage(file, printerId) {
    if (useLocal) {
      // Sem armazenamento local real: retorna placeholder
      return { publicUrl: 'https://placehold.co/600x400?text=Printer', filePath: null };
    }
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${printerId}/${Date.now()}.${fileExt}`;
    const filePath = `printers/${fileName}`;
    const { error: uploadError } = await supabase?.storage?.from('printer-images')?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });
    if (uploadError) throw uploadError;
    const { data: urlData } = supabase?.storage?.from('printer-images')?.getPublicUrl(filePath);
    return { publicUrl: urlData?.publicUrl, filePath };
  },

  async uploadFilamentImage(file, filamentId) {
    if (useLocal) {
      return { publicUrl: 'https://placehold.co/600x400?text=Filament', filePath: null };
    }
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${filamentId}/${Date.now()}.${fileExt}`;
    const filePath = `filaments/${fileName}`;
    const { error: uploadError } = await supabase?.storage?.from('filament-images')?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });
    if (uploadError) throw uploadError;
    const { data: urlData } = supabase?.storage?.from('filament-images')?.getPublicUrl(filePath);
    return { publicUrl: urlData?.publicUrl, filePath };
  },

  async uploadProjectImage(file, projectId) {
    try {
      if (!projectId) throw new Error('projectId é obrigatório');
      if (useLocal) {
        // Armazena em memória como data URL para pré-visualização local
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        window.__projectImages = window.__projectImages || {};
        window.__projectImages[projectId] = [...(window.__projectImages[projectId] || []), dataUrl];
        return { publicUrl: dataUrl, filePath: null };
      }
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${projectId}/${Date.now()}.${fileExt}`;
      const filePath = `projects/${fileName}`;
      const { error: uploadError } = await supabase?.storage?.from('project-images')?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase?.storage?.from('project-images')?.getPublicUrl(filePath);
      return { publicUrl: urlData?.publicUrl, filePath };
    } catch (error) {
      console.error('❌ Erro no upload de imagem do projeto:', error);
      return { publicUrl: null, filePath: null, error };
    }
  },

  async deleteImage(bucketName, filePath) {
    const { error } = await supabase?.storage?.from(bucketName)?.remove([filePath]);
    
    return { error };
  },

  // Filament CRUD with enhanced error handling
  async createFilament(filamentData) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/filaments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(filamentData)
        });
        const data = await resp.json().catch(() => null);
        const error = resp.ok ? null : (data?.error || 'Erro ao criar filamento');
        if (!error) console.log('✅ Filamento criado (local):', `${data?.brand} ${data?.material} ${data?.color}`);
        return { data, error };
      }
      const clean = sanitizeFilamentPayload(filamentData);
      const { data, error } = await supabase?.from('filaments')?.insert([clean])?.select()?.single();
      if (!error) {
        console.log('✅ Filamento criado:', `${data?.brand} ${data?.material} ${data?.color}`);
      }
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao criar filamento:', error);
      return { data: null, error };
    }
  },

  async getUserFilaments(userId) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/users/${userId || 'guest'}/filaments`);
        const data = await resp.json().catch(() => []);
        const error = resp.ok ? null : 'Erro ao buscar filamentos';
        return { data, error };
      }

      const { data, error } = await supabase?.from('filaments')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao buscar filamentos:', error);
      return { data: [], error };
    }
  },

  async updateFilament(filamentId, updates) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/filaments/${filamentId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updates)
        });
        const data = await resp.json().catch(() => null);
        const error = resp.ok ? null : (data?.error || 'Erro ao atualizar filamento');
        if (!error) console.log('✅ Filamento atualizado (local):', filamentId);
        return { data, error };
      }
      const clean = sanitizeFilamentPayload(updates);
      const { data, error } = await supabase?.from('filaments')?.update(clean)?.eq('id', filamentId)?.select()?.single();
      if (!error) {
        console.log('✅ Filamento atualizado:', filamentId);
      }
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao atualizar filamento:', error);
      return { data: null, error };
    }
  },

  async deleteFilament(filamentId) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/filaments/${filamentId}`, { method: 'DELETE' });
        const data = await resp.json().catch(() => null);
        const error = resp.ok ? null : (data?.error || 'Erro ao deletar filamento');
        if (!error) console.log('🗑️ Filamento removido (local):', filamentId);
        return { data, error };
      }

      const { data, error } = await supabase?.from('filaments')?.delete()?.eq('id', filamentId);
      if (!error) {
        console.log('🗑️ Filamento removido:', filamentId);
      }
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao deletar filamento:', error);
      return { data: null, error };
    }
  },

  // Project CRUD with enhanced error handling
  async createProject(projectData) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/projects`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(projectData)
        });
        const data = await resp.json().catch(() => null);
        const error = resp.ok ? null : (data?.error || 'Erro ao criar projeto');
        if (!error) console.log('✅ Projeto criado (local):', data?.name);
        return { data, error };
      }
      const { data, error } = await supabase?.from('printing_projects')?.insert([projectData])?.select()?.single();
      if (!error) {
        console.log('✅ Projeto criado:', data?.name);
      }
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao criar projeto:', error);
      return { data: null, error };
    }
  },

  async getUserProjects(userId) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/users/${userId || 'guest'}/projects`);
        const data = await resp.json().catch(() => []);
        const error = resp.ok ? null : 'Erro ao buscar projetos';
        return { data, error };
      }
      const { data, error } = await supabase?.from('printing_projects')?.select(`
        *,
        printer:printers(name, brand, model),
        project_filaments(
          *,
          filament:filaments(brand, material, color, color_hex, price_per_kg)
        )
      `)?.eq('user_id', userId)?.order('created_at', { ascending: false });
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao buscar projetos:', error);
      return { data: [], error };
    }
  },

  async updateProject(projectId, updates) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/projects/${projectId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updates)
        });
        const data = await resp.json().catch(() => null);
        const error = resp.ok ? null : (data?.error || 'Erro ao atualizar projeto');
        if (!error) console.log('✅ Projeto atualizado (local):', projectId);
        return { data, error };
      }
      const { data, error } = await supabase?.from('printing_projects')?.update(updates)?.eq('id', projectId)?.select()?.single();
      if (!error) {
        console.log('✅ Projeto atualizado:', projectId);
      }
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao atualizar projeto:', error);
      return { data: null, error };
    }
  },

  async deleteProject(projectId) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/projects/${projectId}`, { method: 'DELETE' });
        const data = await resp.json().catch(() => null);
        const error = resp.ok ? null : (data?.error || 'Erro ao deletar projeto');
        if (!error) console.log('🗑️ Projeto removido (local):', projectId);
        return { data, error };
      }
      const { data, error } = await supabase?.from('printing_projects')?.delete()?.eq('id', projectId);
      if (!error) {
        console.log('🗑️ Projeto removido:', projectId);
      }
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao deletar projeto:', error);
      return { data: null, error };
    }
  },

  // Project filaments
  async addProjectFilament(projectFilamentData) {
    try {
      if (useLocal) {
        // Map common fields to local API schema (weight/cost)
        const payload = {
          project_id: projectFilamentData?.project_id,
          filament_id: projectFilamentData?.filament_id,
          // Local API expects `weight` (grams) and `cost`
          weight: Number(projectFilamentData?.weight_grams ?? projectFilamentData?.weight ?? 0),
          cost: Number(projectFilamentData?.cost_amount ?? projectFilamentData?.cost ?? 0),
          // Preserve any additional metadata like waste_grams, notes, etc.
          ...projectFilamentData
        };
        const resp = await fetch(`${apiBase}/project_filaments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await resp.json().catch(() => null);
        const error = resp.ok ? null : (data?.error || 'Erro ao adicionar filamento ao projeto');
        return { data, error };
      }
      // Supabase: limit payload to known columns to avoid insert failures
      const supabasePayload = {
        project_id: projectFilamentData?.project_id,
        filament_id: projectFilamentData?.filament_id,
        weight_grams: Number(projectFilamentData?.weight_grams ?? 0),
        cost_amount: Number(projectFilamentData?.cost_amount ?? 0)
      };
      const { data, error } = await supabase?.from('project_filaments')?.insert([supabasePayload])?.select()?.single();
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao adicionar filamento ao projeto:', error);
      return { data: null, error };
    }
  },

  async removeProjectFilament(projectFilamentId) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/project_filaments/${projectFilamentId}`, { method: 'DELETE' });
        const data = await resp.json().catch(() => null);
        const error = resp.ok ? null : (data?.error || 'Erro ao remover filamento do projeto');
        return { data, error };
      }
      const { data, error } = await supabase?.from('project_filaments')?.delete()?.eq('id', projectFilamentId);
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao remover filamento do projeto:', error);
      return { data: null, error };
    }
  },

  // Aggregations: Marketplace revenue and fees per platform
  async getMarketplaceAggregates(userId, { startDate, endDate, statusFilter = 'all' } = {}) {
    try {
      if (useLocal) {
        // Fallback simples em modo local
        return { data: { byMarketplace: [], totals: { units: 0, revenue: 0, fees: 0 } }, error: null };
      }
      const { data: projects, error: projectsError } = await supabase?.from('printing_projects')
        ?.select('id, quantity, final_price, price_per_unit, status, created_at')
        ?.eq('user_id', userId)
        ?.gte('created_at', startDate)
        ?.lte('created_at', endDate);
      if (projectsError) return { data: null, error: projectsError };

      const projectIds = (projects || [])?.map(p => p?.id);
      if (!projectIds?.length) {
        return { data: { byMarketplace: [], totals: { units: 0, revenue: 0, fees: 0 } }, error: null };
      }

      const { data: relations, error: relError } = await supabase?.from('project_marketplaces')
        ?.select('project_id, marketplace_id, quantity, applied_fee_percent, applied_fixed_fee_per_unit')
        ?.in('project_id', projectIds);
      if (relError) return { data: null, error: relError };

      const projectMap = new Map((projects || [])?.map(p => [p?.id, p]));
      const agg = new Map();
      const totals = { units: 0, revenue: 0, fees: 0 };
      for (const rel of relations || []) {
        const p = projectMap.get(rel?.project_id);
        if (!p) continue;
        if (statusFilter !== 'all' && (p?.status) !== statusFilter) continue;
        const units = Number(rel?.quantity || 0);
        const pricePerUnit = Number(p?.price_per_unit || ((p?.final_price && p?.quantity) ? (Number(p?.final_price) / Number(p?.quantity)) : 0));
        const revenue = units * pricePerUnit;
        const percentFee = Number(rel?.applied_fee_percent || 0);
        const fixedFee = Number(rel?.applied_fixed_fee_per_unit || 0);
        const fees = revenue * (percentFee / 100) + (units * fixedFee);

        const key = rel?.marketplace_id;
        const current = agg.get(key) || { marketplace_id: key, units: 0, revenue: 0, fees: 0 };
        current.units += units;
        current.revenue += revenue;
        current.fees += fees;
        agg.set(key, current);

        totals.units += units;
        totals.revenue += revenue;
        totals.fees += fees;
      }

      const byMarketplace = Array.from(agg.values());
      const marketplaceIds = byMarketplace?.map(x => x?.marketplace_id).filter(Boolean);
      let namesMap = new Map();
      if (marketplaceIds?.length) {
        const { data: mks } = await supabase?.from('marketplaces')?.select('id, name')?.in('id', marketplaceIds);
        if (Array.isArray(mks)) {
          namesMap = new Map(mks?.map(m => [m?.id, m?.name]));
        }
      }

      const result = byMarketplace?.map(x => ({
        ...x,
        marketplace_name: namesMap?.get(x?.marketplace_id) || 'Marketplace'
      }));

      return { data: { byMarketplace: result, totals }, error: null };
    } catch (error) {
      console.error('❌ Erro na agregação de marketplaces:', error);
      return { data: null, error };
    }
  },

  // Aggregations: Material consumption per material type
  async getMaterialConsumption(userId, { startDate, endDate } = {}) {
    try {
      if (useLocal) {
        return { data: [], byMonth: [], error: null };
      }
      const { data: projects, error: projectsError } = await supabase?.from('printing_projects')
        ?.select('id, created_at, estimated_duration_hours')
        ?.eq('user_id', userId)
        ?.gte('created_at', startDate)
        ?.lte('created_at', endDate);
      if (projectsError) return { data: null, error: projectsError };
      const projectIds = (projects || [])?.map(p => p?.id);
      if (!projectIds?.length) return { data: [], byMonth: [], error: null };

      const { data: pfRows, error: pfError } = await supabase?.from('project_filaments')
        ?.select('project_id, filament_id, weight_grams, cost_amount')
        ?.in('project_id', projectIds);
      if (pfError) return { data: null, error: pfError };

      const filamentIds = Array.from(new Set((pfRows || [])?.map(r => r?.filament_id).filter(Boolean)));
      let materialsMap = new Map();
      if (filamentIds?.length) {
        const { data: filaments } = await supabase?.from('filaments')?.select('id, material')?.eq('user_id', userId)?.in('id', filamentIds);
        if (Array.isArray(filaments)) {
          materialsMap = new Map(filaments?.map(f => [f?.id, f?.material]));
        }
      }

      const projectMap = new Map((projects || [])?.map(p => [p?.id, { created_at: p?.created_at, hours: Number(p?.estimated_duration_hours || 0) }]));
      const seenProjectMaterial = new Set(); // to count hours per project per material only once
      const agg = new Map();
      const monthlyAgg = new Map(); // key: `${material}|${YYYY-MM}`
      const hoursByMaterial = new Map();
      for (const row of pfRows || []) {
        const mat = materialsMap?.get(row?.filament_id) || 'DESCONHECIDO';
        const grams = Number(row?.weight_grams || 0);
        const cost = Number(row?.cost_amount || 0);
        const curr = agg.get(mat) || { material: mat, grams: 0, cost: 0 };
        curr.grams += grams;
        curr.cost += cost;
        agg.set(mat, curr);

        // per-month breakdown
        const pinfo = projectMap.get(row?.project_id);
        const monthKey = pinfo?.created_at ? new Date(pinfo?.created_at).toISOString().slice(0, 7) : null; // YYYY-MM
        if (monthKey) {
          const mk = `${mat}|${monthKey}`;
          const mcurr = monthlyAgg.get(mk) || { material: mat, month: monthKey, grams: 0, cost: 0, hours: 0 };
          mcurr.grams += grams;
          mcurr.cost += cost;
          monthlyAgg.set(mk, mcurr);
        }

        // hours per material (count each project once per material)
        const projHours = Number(pinfo?.hours || 0);
        const spKey = `${row?.project_id}|${mat}`;
        if (!seenProjectMaterial.has(spKey)) {
          hoursByMaterial.set(mat, (hoursByMaterial.get(mat) || 0) + projHours);
          // also add to monthlyAgg hours once per project-material
          if (monthKey) {
            const mk = `${mat}|${monthKey}`;
            const mcurr = monthlyAgg.get(mk) || { material: mat, month: monthKey, grams: 0, cost: 0, hours: 0 };
            mcurr.hours += projHours;
            monthlyAgg.set(mk, mcurr);
          }
          seenProjectMaterial.add(spKey);
        }
      }

      const result = Array.from(agg.values())?.map(item => {
        const hours = Number(hoursByMaterial.get(item.material) || 0);
        const gramsPerHour = hours > 0 ? (item.grams / hours) : 0;
        const costPerGram = item.grams > 0 ? (item.cost / item.grams) : 0;
        return { ...item, hours, grams_per_hour: gramsPerHour, cost_per_gram: costPerGram };
      });

      return { data: result, byMonth: Array.from(monthlyAgg.values()), error: null };
    } catch (error) {
      console.error('❌ Erro na agregação de consumo de materiais:', error);
      return { data: null, error };
    }
  },

  // Aggregations: Printer utilization (active vs maintenance vs inactive)
  async getPrinterUtilization(userId, { startDate, endDate } = {}) {
    try {
      if (useLocal) {
        return { data: [], error: null };
      }
      const { data: printers, error: printersError } = await supabase?.from('printers')
        ?.select('id, name')
        ?.eq('user_id', userId);
      if (printersError) return { data: null, error: printersError };

      const results = [];
      for (const printer of (printers || [])) {
        const { data: events, error: eventsError } = await supabase?.from('printer_status_history')
          ?.select('old_status, new_status, changed_at')
          ?.eq('printer_id', printer?.id)
          ?.gte('changed_at', startDate)
          ?.lte('changed_at', endDate)
          ?.order('changed_at', { ascending: true });
        if (eventsError) return { data: null, error: eventsError };

        const { data: prevEvent, error: prevError } = await supabase?.from('printer_status_history')
          ?.select('new_status, changed_at')
          ?.eq('printer_id', printer?.id)
          ?.lt('changed_at', startDate)
          ?.order('changed_at', { ascending: false })
          ?.limit(1);
        if (prevError) return { data: null, error: prevError };

        const start = new Date(startDate);
        const end = new Date(endDate);
        let timeline = [];
        let currentStatus = prevEvent?.[0]?.new_status || 'active';
        let currentTime = start;
        let failureCount = 0;
        let lastFailureTime = null;
        for (const ev of (events || [])) {
          const changeTime = new Date(ev?.changed_at);
          if (changeTime < start) continue;
          const deltaHours = Math.max(0, (changeTime - currentTime) / 36e5);
          timeline.push({ status: currentStatus, hours: deltaHours });
          // Contabilizar falhas: transição para manutenção como início de falha
          if (currentStatus === 'active' && ev?.new_status === 'maintenance') {
            failureCount += 1;
            lastFailureTime = changeTime;
          }
          currentStatus = ev?.new_status;
          currentTime = changeTime;
        }
        const finalDelta = Math.max(0, (end - currentTime) / 36e5);
        timeline.push({ status: currentStatus, hours: finalDelta });

        const sum = timeline?.reduce((acc, t) => {
          acc[t?.status] = (acc[t?.status] || 0) + t?.hours;
          return acc;
        }, {});
        const total = Object.values(sum || {})?.reduce((a, b) => a + b, 0) || 0;
        const pct = (h) => total > 0 ? (h / total) * 100 : 0;
        const activeHours = Number(sum?.['active'] || 0);
        const maintenanceHours = Number(sum?.['maintenance'] || 0);
        const inactiveHours = Number(sum?.['inactive'] || 0);
        const downtimeHours = maintenanceHours + inactiveHours;
        const availabilityPct = pct(activeHours);
        const mtbfHours = failureCount > 0 ? (activeHours / failureCount) : null;
        results.push({
          printer_id: printer?.id,
          printer_name: printer?.name,
          active_hours: activeHours,
          maintenance_hours: maintenanceHours,
          inactive_hours: inactiveHours,
          active_pct: pct(activeHours),
          maintenance_pct: pct(maintenanceHours),
          inactive_pct: pct(inactiveHours),
          availability_pct: availabilityPct,
          mtbf_hours: mtbfHours,
          downtime_hours: downtimeHours,
          failure_count: failureCount
        });
      }

      return { data: results, error: null };
    } catch (error) {
      console.error('❌ Erro na agregação de utilização de impressoras:', error);
      return { data: null, error };
    }
  },

  // Analytics helpers with enhanced calculations
  async getUserAnalytics(userId) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/users/${userId || 'guest'}/projects`);
        const projects = await resp.json().catch(() => []);
        if (!resp.ok) {
          const error = 'Erro ao buscar análise';
          return { data: null, error };
        }
        const totalRevenue = projects?.reduce((sum, p) => sum + (parseFloat(p?.final_price) || 0), 0) || 0;
        const totalProfit = projects?.reduce((sum, p) => sum + (parseFloat(p?.profit_amount) || 0), 0) || 0;
        const completedProjects = projects?.filter(p => p?.status === 'completed')?.length || 0;
        const activeProjects = projects?.filter(p => p?.status === 'in_progress')?.length || 0;
        console.log('📊 Análise calculada (local):', { totalRevenue, totalProfit, completedProjects, activeProjects });
        return {
          data: {
            totalRevenue,
            totalProfit,
            completedProjects,
            activeProjects,
            projects
          },
          error: null
        };
      }
      const { data: projects, error: projectsError } = await supabase?.from('printing_projects')?.select(`
        id,
        name,
        printer_id,
        final_price,
        profit_amount,
        status,
        created_at,
        estimated_duration_hours,
        image_url,
        printer:printers(name, brand, model)
      `)?.eq('user_id', userId)?.order('created_at', { ascending: false });
      if (projectsError) {
        console.error('❌ Erro ao buscar análise:', projectsError);
        return { data: null, error: projectsError };
      }
      const totalRevenue = projects?.reduce((sum, p) => sum + (parseFloat(p?.final_price) || 0), 0) || 0;
      const totalProfit = projects?.reduce((sum, p) => sum + (parseFloat(p?.profit_amount) || 0), 0) || 0;
      const completedProjects = projects?.filter(p => p?.status === 'completed')?.length || 0;
      const activeProjects = projects?.filter(p => p?.status === 'in_progress')?.length || 0;
      console.log('📊 Análise calculada:', { totalRevenue, totalProfit, completedProjects, activeProjects });
      return {
        data: {
          totalRevenue,
          totalProfit,
          completedProjects,
          activeProjects,
          projects
        },
        error: null
      };
    } catch (error) {
      console.error('❌ Erro crítico na análise:', error);
      return { data: null, error };
    }
  }
};

export default supabase;